import React, { useEffect, useState } from "react";
import { supabase, getFunctionsUrl, getAccessToken } from "../lib/supabaseClient";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function AcceptInvitePage() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const emailParam = sp.get("email") || "";
  const [email, setEmail] = useState(emailParam);
  const [consent, setConsent] = useState(false);
  const [step, setStep] = useState("loading"); // 'loading' | 'invalid' | 'auth' | 'consent' | 'done'
  const [error, setError] = useState("");
  const [invite, setInvite] = useState(null); // { advisor_name, advisor_title, status, ... }
  const nav = useNavigate();

  // Charge les infos publiques de l'invitation (nom de l'advisor, validité) avant
  // même la connexion, via advisor-invite-info (lecture par token, sans JWT requis).
  useEffect(() => {
    (async () => {
      if (!token) { setStep("invalid"); setError("Lien d'invitation invalide."); return; }
      try {
        const resp = await fetch(getFunctionsUrl("advisor-invite-info"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        const json = await resp.json();
        if (!resp.ok) {
          setStep("invalid");
          setError(json?.error || "Invitation introuvable.");
          return;
        }
        if (json.status === "expired") {
          setStep("invalid");
          setError("Cette invitation a expiré. Demandez à votre Art Advisor de vous en envoyer une nouvelle.");
          return;
        }
        if (json.status !== "pending") {
          setStep("invalid");
          setError(json.status === "accepted" ? "Cette invitation a déjà été acceptée." : "Cette invitation a déjà été refusée.");
          return;
        }
        setInvite(json);

        const { data: { user } } = await supabase.auth.getUser();
        setStep(user ? "consent" : "auth");
      } catch (e) {
        setStep("invalid");
        setError("Impossible de vérifier cette invitation pour le moment.");
      }
    })();
  }, [token]);

  const advisorLabel = invite?.advisor_name
    ? `${invite.advisor_name}${invite.advisor_title ? ` (${invite.advisor_title})` : ""}`
    : "Votre Art Advisor";

  const signUpOrIn = async (e) => {
    e.preventDefault();
    setError("");
    try {
      // choix simple: magic link (pas besoin de mdp) :
      const { error } = await supabase.auth.signInWithOtp({ email, options: { emailRedirectTo: window.location.href } });
      if (error) throw error;
      alert("Vérifie ta boîte mail pour te connecter. Reviens ensuite sur cette page.");
    } catch (e) { setError(e.message || String(e)); }
  };

  const accept = async () => {
    setError("");
    if (!consent) { setError("Veuillez cocher la case de consentement."); return; }
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { setError("Veuillez vous connecter d’abord."); return; }
    const tokenAuth = await getAccessToken();
    const resp = await fetch(getFunctionsUrl("advisor-invites-accept"), {
      method: "POST",
      headers: { "Content-Type":"application/json", Authorization: `Bearer ${tokenAuth}` },
      body: JSON.stringify({ token, consent: true })
    });
    const json = await resp.json();
    if (!resp.ok) { setError(json?.error || "Échec de l’acceptation"); return; }
    setStep("done");
  };

  const decline = async () => {
    const tokenAuth = await getAccessToken();
    const resp = await fetch(getFunctionsUrl("advisor-invites-decline"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${tokenAuth}` },
      body: JSON.stringify({ token }),
    });
    if (resp.ok) { nav("/"); } else { const j = await resp.json(); setError(j?.error || "Échec du refus"); }
  };

  return (
    <div className="max-w-lg mx-auto p-6">
      <h1 className="text-2xl font-light mb-4">Invitation Artkiv</h1>

      {step === "loading" && (
        <p className="text-sm text-gray-500">Vérification de l'invitation…</p>
      )}

      {step === "invalid" && (
        <div className="space-y-3">
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 p-3 rounded">{error}</p>
          <button onClick={() => nav("/")} className="px-3 py-2 border rounded-lg text-sm">Retour à l'accueil</button>
        </div>
      )}

      {step === "auth" && (
        <form onSubmit={signUpOrIn} className="space-y-4">
          <p className="text-gray-700"><strong>{advisorLabel}</strong> vous invite à gérer votre collection sur Artkiv.</p>
          <label className="text-sm text-gray-600">Votre email</label>
          <input required type="email" value={email} onChange={e=>setEmail(e.target.value)}
                 className="w-full border rounded-lg px-3 py-2" />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button className="px-3 py-2 rounded-lg bg-black text-white text-sm">Recevoir un lien de connexion</button>
          <p className="text-xs text-gray-500">En vous connectant, vous pourrez accepter l’invitation.</p>
        </form>
      )}

      {step === "consent" && (
        <div className="space-y-4">
          <div className="p-4 border rounded-lg">
            <p className="text-sm text-gray-700">
              <strong>{advisorLabel}</strong> vous propose de centraliser et sécuriser votre collection d’art sur <strong>Artkiv</strong>.<br/>
              En acceptant, vous créez votre espace <em>(plan Standard)</em> et autorisez <strong>{advisorLabel}</strong> à co-gérer votre collection (ajouts, rapports, suivi).<br/>
              Vous pourrez retirer cet accès à tout moment depuis vos paramètres.
            </p>
          </div>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={consent} onChange={e=>setConsent(e.target.checked)} />
            <span>J’autorise explicitement l’accès de mon Art Advisor à ma collection Artkiv.</span>
          </label>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2">
            <button onClick={accept} className="px-3 py-2 bg-black text-white rounded-lg text-sm">Accepter</button>
            <button onClick={decline} className="px-3 py-2 border rounded-lg text-sm">Refuser</button>
          </div>
        </div>
      )}

      {step === "done" && (
        <div className="space-y-3">
          <p className="text-green-700 bg-green-50 border border-green-200 p-3 rounded">Invitation acceptée. Votre espace Standard est provisionné.</p>
          <button onClick={()=>nav("/dashboard")} className="px-3 py-2 bg-black text-white rounded-lg text-sm">Aller à mon Dashboard</button>
        </div>
      )}
    </div>
  );
}
